import {booleanAttribute, Directive, DoCheck, ElementRef, input, OnChanges, SimpleChanges} from '@angular/core';
import {IconType} from '@shared-types';

import {IconIfDirective} from './icon-if.directive';

@Directive({
	selector: '[dc-icon]',
	standalone: true,
})
export class IconDirective implements DoCheck, OnChanges {
	readonly color = input<string>(undefined);
	readonly primary = input<string>(undefined);
	readonly secondary = input<string>(undefined);
	readonly size = input<'2xs' | 'xs' | 'sm' | 'lg' | 'xl' | '2x' | '3x' | '4x' | '5x' | '6x' | '7x' | '8x' | '9x' | '10x'>(undefined);
	readonly animation = input<'beat' | 'beat-fade' | 'bounce' | 'fade' | 'shake' | 'spin' | 'spin-reverse' | 'spin-pulse'>(undefined);
	readonly animationCount = input<number>(undefined);
	readonly scale = input<number>(undefined);
	readonly faType = input<IconType>(undefined);
	readonly faName = input<string>(undefined, {alias: 'dc-icon'});
	readonly fw = input<boolean, unknown>(undefined, {transform: booleanAttribute});
	private _changedProperties: Set<keyof IconIfDirective> = new Set<keyof IconIfDirective>();
	private id: string = undefined;
	private rendered = false;

	constructor(
		private elementRef: ElementRef,
		private iconIf: IconIfDirective,
	) {}

	ngDoCheck(): void {
		if (this.iconIf) {
			if (!this.id) {
				this.id = this.iconIf.id;
			}
			const ref = this.iconIf.ref;
			if (ref) {
				const parent = ref.parentElement;
				if (parent) {
					const id = this.id;
					if (id) {
						const sameID = parent.querySelectorAll(`[id="${id}"]`);
						if (sameID.length > 1) {
							sameID.forEach((el, index) => {
								if (index !== sameID.length - 1) {
									el.remove();
								}
							});
						}
					}
				}
			}

			if (ref && (this._changedProperties.size > 0 || !this.rendered)) {
				const faType = this.faType();
				const faName = this.faName();
				if (faType && faName) ref.classList.add(`fa-${faType}`, `fa-${faName}`);

				const color = this.color();
				if (color) ref.style.color = color;
				const primary = this.primary();
				if (primary) ref.style.setProperty('--fa-primary-color', primary);
				const secondary = this.secondary();
				if (secondary) ref.style.setProperty('--fa-secondary-color', secondary);

				const size = this.size();
				if (size) ref.classList.add(`fa-${size}`);

				if (this.fw()) ref.classList.add('fa-fw');

				const animation = this.animation();
				if (animation && !this.rendered) {
					ref.classList.add(`fa-${animation}`);
					const animationCount = this.animationCount();
					if (animationCount) ref.style['--fa-animation-iteration-count'] = animationCount.toString();
				}

				const scale = this.scale();
				if (scale) ref.style['--fa-scale'] = scale.toString();

				this.rendered = true;
				this._changedProperties.clear();
			}
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		for (const key in changes) {
			if (changes.hasOwnProperty(key) && changes[key].currentValue !== changes[key].previousValue) {
				this._changedProperties.add(key as keyof IconIfDirective);
			}
		}
	}
}
